import React, { type ComponentType, type ReactElement } from 'react';
import type { ProjectType } from '@atlassian/jira-common-constants';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { extractProjectKey } from '@atlassian/jira-issue-fetch-services-common/src/common/utils/extract-project-key.tsx';
import type { ContainerContextSubscriberType } from '@atlassian/jira-providers-container-context/src/main.tsx';
import type { ContainerContext } from '@atlassian/jira-providers-container-context/src/types.tsx';
import {
	toProjectId,
	type BaseUrl,
	type IssueKey,
	type IssueTypeId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { isWorkflowDataServiceHookEnabled } from '../../feature-flags';
import { useProjectViewWorkflowService } from './use-project-view-workflow-service/main';
import {
	ViewWorkflowService as ViewWorkflowServiceDI,
	type Props as ViewWorkflowServiceProps,
} from './workflow-data-service';

type WorkflowServiceData = {
	hasPermission?: boolean;
	issueTypeId?: IssueTypeId;
	projectId?: string | undefined;
	projectType?: ProjectType | undefined;
	isSimplified?: boolean | undefined;
};

export type ServiceApi = {
	data: WorkflowServiceData | undefined;
	loading: boolean;
	error: FetchError | undefined;
	fetch: () => Promise<void>;
};

// ff use-view-workflow-service-hook_8y6bk cleanup: remove ViewWorkflowService and ContainerContextSubscriber and children from props (unused by hook)
export type Props = {
	issueKey: IssueKey;
	baseUrl: BaseUrl;
	ViewWorkflowService: ComponentType<ViewWorkflowServiceProps>;
	ContainerContextSubscriber: ContainerContextSubscriberType;
	children: (arg1: ServiceApi) => ReactElement;
};

// ff use-view-workflow-service-hook_8y6bk cleanup: remove getIssueProjectDataFromContext
export const getIssueProjectDataFromContext = (
	containerContextData: ContainerContext | null,
	issueProjectKey: string,
): WorkflowServiceData | undefined => {
	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	if (!containerContextData || !containerContextData.project) return undefined;

	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	const contextProject = containerContextData.project;

	const hasIssueProjectDataInContext = contextProject.key === issueProjectKey;
	if (!hasIssueProjectDataInContext) return undefined;

	return {
		projectId: toProjectId(contextProject.id.toString()),
		projectType: contextProject.type,
		isSimplified: contextProject.isSimplified,
	};
};

export const emptyResult: ServiceApi = {
	data: undefined,
	loading: false,
	error: undefined,
	fetch: () => Promise.resolve(),
};

/**
 * Gets projectData from ContainerContextSubscriber or from service
 */
const ViewWorkflowContextService = (props: Props) => {
	const { ViewWorkflowService, ContainerContextSubscriber, baseUrl, issueKey, children } = props;

	if (isWorkflowDataServiceHookEnabled()) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return children(useProjectViewWorkflowService(baseUrl, issueKey));
	}

	// ff use-view-workflow-service-hook_8y6bk cleanup: remove return below and unwrap ff conditional above
	const issueProjectKey = issueKey ? extractProjectKey(issueKey) : null;
	if (!issueKey || !issueProjectKey) {
		return children({ ...emptyResult });
	}

	return (
		<ContainerContextSubscriber>
			{({ data: containerContextData }) => {
				const contextData = getIssueProjectDataFromContext(containerContextData, issueProjectKey);

				return (
					<ViewWorkflowService
						baseUrl={baseUrl}
						issueKey={issueKey}
						projectKey={contextData ? undefined : issueProjectKey}
						isSimplified={contextData && contextData.isSimplified}
					>
						{({ data: serviceData, ...serviceProps }) => {
							const data =
								contextData || serviceData
									? {
											...contextData,
											...serviceData,
										}
									: undefined;
							return children({ data, ...serviceProps });
						}}
					</ViewWorkflowService>
				);
			}}
		</ContainerContextSubscriber>
	);
};

ViewWorkflowContextService.defaultProps = {
	ViewWorkflowService: ViewWorkflowServiceDI,
};

export default ViewWorkflowContextService;
